import { StyleSheet, View, Pressable, Image, FlatList, Platform, Linking } from "react-native";
import RNMapbox, { StyleURL } from '@rnmapbox/maps';
import type { Camera } from '@rnmapbox/maps';
import type { Position } from "@rnmapbox/maps/lib/typescript/types/Position";
import { useContext, useEffect, useRef, useState } from "react";
import Icon from 'react-native-vector-icons/Ionicons';
import { PlaceColors } from "./helpers/PlaceColors";
import { BasePage } from "./helpers/BasePage";

import React from "react";
import { FindifyDataContext, FindifyDataContextType } from "@/contexts/FindifyDataContext";
import { FindifyBasicModal } from "./components/FindifyBasicModal";
import { makeIconFilterButton } from "./helpers/Components";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { PlaceText } from "./components/PlaceText";
import { FindifyToast } from "./components/FindifyToast";
import { DefaultShadow, IconShadow, TextShadow } from "./helpers/DefaultShadows";
import { Toast } from "react-native-toast-message/lib/src/Toast";
import { getTranslatedString } from "./helpers/FindifyTranslatedString";
import { BottomNavigationPadding } from "./components/FindifyNavigation";
import { useTranslation } from "react-i18next";
import type { CameraBoundsWithPadding } from "@rnmapbox/maps/lib/typescript/components/Camera";
import { FindifyLoadingScreen } from "./components/FindifyLoadingScreen";
import { useRoute } from "@react-navigation/native";
import { getApiFromModule } from "./helpers/GetContentsByModule";
import { getFeaturedImageUrl } from "./helpers/DetailsHelpers";

const getCoordsFromPOINT = (pointGeometry: any) => {
    if (pointGeometry) {
        const coordinatesMatch = pointGeometry?.match(/\(([^)]+)\)/);

        if (coordinatesMatch) {
            // Split the coordinates string into an array
            const coordinatesArray = coordinatesMatch[1].split(' ');

            // Extract latitude and longitude
            const latitude = parseFloat(coordinatesArray[0]);
            const longitude = parseFloat(coordinatesArray[1]);

            // console.log(`Latitude: ${latitude}, Longitude: ${longitude}`);
            return { latitude, longitude };
        } else {
            console.log('Invalid POINT geometry');
        }
    }

    return {};
}


const hasMatchingNumbers = (arr1: number[], arr2: number[]) => {
    const numSet: any = {};

    for (const num of arr1) {
        numSet[num] = true;
    }

    for (const num of arr2) {
        if (numSet[num]) {
            return true; // Matching number found
        }
    }

    return false; // No matching numbers
};

const loadInitialPosition = (findifyDataContext: FindifyDataContextType, setInitialPosition: Function, setMapCenter: Function, setMapZoom: Function, setMapPitch: Function, setMapBearing: Function) => {
    const mapData = findifyDataContext.workspace?.app_settings?.map ?? {};
    const initialPositionCoords = [
        +(mapData?.longitude ?? 0),
        +(mapData?.latitude ?? 0),
    ];
    setInitialPosition(initialPositionCoords);
    setMapCenter(initialPositionCoords)
    setMapZoom(+(mapData.zoom ?? 14.5));
    setMapPitch(+(mapData.pitch ?? 0));
    setMapBearing(+(mapData.bearing ?? 0));
}


const OnMapPoiClick = (poi: any, setSelectedPOI: Function, navigation: any, findifyDataContext: FindifyDataContextType) => {
    setSelectedPOI(poi.point_of_interest_id);
    FindifyToast({
        type: 'childToast',
        props: {
            toast: <MapPlaceToast
                title={getTranslatedString(poi.title, findifyDataContext)}
                message={poi.address}
                image={getFeaturedImageUrl(poi)}
                onPress={() => {
                    navigation.push('PlaceContentDetails', {
                        module: 'points_of_interest',
                        content_id: poi.point_of_interest_id,
                        page_slug: poi.page_slug,
                    });
                    Toast.hide();
                    setSelectedPOI(undefined);
                }}
                onHide={() => {
                    setSelectedPOI(undefined);
                }
                }
                findifyDataContext={findifyDataContext}
            />
        },
        position: 'bottom',
        autoHide: false,
    })
}
const PlaceMapElement = ({ navigation,
    availablePois,
    availablePOISFiltered,
    availablePOISCategories,
    selectedCategories,

    selectedPOI,
    setSelectedPOI,

    focusedPoiId,
    focusedCoordinates,

    setSelectedCategories }: {
        navigation: any,
        availablePois: any[],
        availablePOISFiltered: any[],
        availablePOISCategories: any[],
        selectedCategories: any[],
        setSelectedCategories: Function,
        selectedPOI: number,
        setSelectedPOI: Function,
        focusedPoiId?: number,
        focusedCoordinates?: Position,
    }) => {
    RNMapbox.setAccessToken("pk.eyJ1IjoidGlhZ28tcGludG8iLCJhIjoiY2wzc3Y2NnQyMDZxMzNkcnhheXNxcWs2dyJ9.higjHxFvD1PTCRkljddWig");
    // HOW TO GET A ROUTE FROM TWO COORDINATES
    // https://api.mapbox.com/directions/v5/mapbox/walking/-8.384848,41.561304;-8.383554,41.562651;-8.407502,41.557948;-8.394443,41.556063?geometries=geojson&access_token=pk.eyJ1IjoidGlhZ28tcGludG8iLCJhIjoiY2wzc3Y2NnQyMDZxMzNkcnhheXNxcWs2dyJ9.higjHxFvD1PTCRkljddWig

    const findifyDataContext = useContext(FindifyDataContext);
    const insets = useSafeAreaInsets();
    const [mapMode,
        setMapMode] = useState<StyleURL>(StyleURL.Light);
    const [initialPosition, setInitialPosition] = useState([
        0,
        0,
    ]); // TODO Load from api
    const [mapCenter, setMapCenter] = useState<Position>(initialPosition)
    const [mapZoom, setMapZoom] = useState<number>(14.5);
    const [mapPitch, setMapPitch] = useState<number>(0);
    const [mapBearing, setMapBearing] = useState<number>(0);
    const [mapBoundPadding, setMapBoundPadding] = useState<RNMapbox.CameraPadding>({
        paddingLeft: 0.00001,
        paddingRight: 0.00001,
        paddingTop: 0.00001,
        paddingBottom: 0.00001,
    });
    const [mapBounds, setMapBounds] = useState<CameraBoundsWithPadding>({ sw: [0, 0], ne: [0, 0], ...mapBoundPadding });
    const [currentUserLocation, setCurrentUserLocation] = useState<number[] | [number, number]>([0, 0]);
    const [currentMapCameraState, setCurrentMapCameraState] = useState<RNMapbox.MapState | undefined>();
    const [showPath, setShowPath] = useState(false)
    const [loaded, setLoaded] = useState<boolean>(false);
    const camera = useRef<Camera>();

    useEffect(() => {
        setMapMode(findifyDataContext?.darkMode ? StyleURL.Dark : StyleURL.Light);
        loadInitialPosition(findifyDataContext, setInitialPosition, setMapCenter, setMapZoom, setMapPitch, setMapBearing);
        setLoaded(true);
    }, []);


    useEffect(() => {
        let maxLat = -200;
        let minLat = 200;
        let maxLong = -200;
        let minLong = 200;
        let loadCenter = false;
        let lat = 0;
        let long = 0;
        let count = 0;
        if (initialPosition[0] === 0 && initialPosition[1] === 0) {
            loadCenter = true;
        }
        availablePois.map((poi: any) => {
            if (poi.latitude && poi.longitude) {
                if (loadCenter) {
                    lat += +poi.latitude;
                    long += +poi.longitude;
                    count++;
                }
                if (+poi.latitude > maxLat) {
                    maxLat = +poi.latitude;
                }
                if (+poi.latitude < minLat) {
                    minLat = +poi.latitude;
                }
                if (+poi.longitude > maxLong) {
                    maxLong = +poi.longitude;
                }
                if (+poi.longitude < minLong) {
                    minLong = +poi.longitude;
                }
            }
        });
        if (loadCenter && count > 0) {
            const mapCenterLat = lat / count;
            const mapCenterLong = long / count;
            setInitialPosition([mapCenterLong, mapCenterLat]);
            setMapCenter([mapCenterLong, mapCenterLat]);
        }
        setMapBounds({
            sw: [minLong, minLat],
            ne: [maxLong, maxLat],
            ...mapBoundPadding,
        })
    }, [availablePois])

    useEffect(() => {
        if (!focusedCoordinates?.length) return;
        setSelectedPOI(focusedPoiId);
        setMapCenter(focusedCoordinates);
        setMapZoom(17);
    }, [focusedPoiId, focusedCoordinates?.[0], focusedCoordinates?.[1]]);

    if (!loaded) return <FindifyLoadingScreen />;
    return <View style={styles.container}>
        <RNMapbox.MapView
            styleURL={mapMode}
            compassEnabled={true}
            compassPosition={{ top: insets.top + 8, right: 8 }}
            scaleBarEnabled={true}
            onCameraChanged={(event) => {
                setCurrentMapCameraState(event);
            }}
            style={styles.map}
        >
            <RNMapbox.Camera
                ref={camera}
                pitch={mapPitch}
                heading={mapBearing}
                zoomLevel={mapZoom}
                bounds={mapBounds}
                padding={mapBoundPadding}
                centerCoordinate={mapCenter}
                animationMode="flyTo"
                animationDuration={2000}
            />


            {/* PATHS */}

            {/* 
    <RNMapbox.ShapeSource
        id="dummyPath"
        shape={MapboxDirectionsApiPathCar.routes[0].geometry as any}
    >

    </RNMapbox.ShapeSource>
    {
        showPath && <RNMapbox.LineLayer
            style={{
                lineWidth: 3,

                // lineColor: PlaceColors(findifyDataContext).primary.get(),
                lineColor: PlaceColors(findifyDataContext).primary.get(),
            }}
            id="_dummyPath"
            sourceID="dummyPath"
        />
    }
   */}

            <RNMapbox.UserLocation
                onUpdate={(event) => {
                    setCurrentUserLocation([event.coords.longitude, event.coords.latitude]);
                }}
            />

            {/* // POIS */}
            {availablePOISFiltered.map((poi: any, index: number) => {
                if (poi.latitude && poi.longitude) {
                    return <RNMapbox.MarkerView
                        key={index}
                        allowOverlap={true}
                        id={'poi_' + poi.point_of_interest_id}
                        coordinate={[poi.longitude, poi.latitude]}
                        anchor={{ x: 0.5, y: 0.5 }}
                        isSelected={selectedPOI === poi.point_of_interest_id}
                    >
                        <Pressable
                            onPress={() => {
                                OnMapPoiClick(poi, setSelectedPOI, navigation, findifyDataContext);
                            }}
                        >
                            <View style={{
                                backgroundColor:
                                    selectedPOI === poi.point_of_interest_id ? PlaceColors(findifyDataContext).background.get() : PlaceColors(findifyDataContext).text.get(),
                                padding: 2,
                                zIndex: selectedPOI === poi.point_of_interest_id ? 100 : 0,
                                borderRadius: 100,
                            }}>
                                <Icon name="location-outline" size={20} color={
                                    selectedPOI === poi.point_of_interest_id ? PlaceColors(findifyDataContext).text.get() : PlaceColors(findifyDataContext).background.get()} />
                            </View>
                        </Pressable>
                    </RNMapbox.MarkerView>
                }
            })}


        </RNMapbox.MapView>
        <View style={styles.mapCompassButton}>
            <Icon name="navigate" size={38} color="#f28d78" />
        </View>
        <View style={[styles.mapButtons, { bottom: insets.bottom + 60 }]}>
            {makeIconFilterButton(() => {
                camera.current.setCamera({
                    zoomLevel: mapZoom,
                    heading: mapBearing,
                    pitch: mapPitch,
                    centerCoordinate: initialPosition,
                });
            }, "home-outline", false, findifyDataContext)}
            {makeIconFilterButton(() => setMapCenter(currentUserLocation), "locate-outline", false, findifyDataContext)}
            {/* {makeIconFilterButton(() => setShowPath(!showPath), "analytics-outline", showPath, findifyDataContext)} */}

            {makeIconFilterButton(() => {
                setMapMode(mapMode === StyleURL.Satellite ? (findifyDataContext?.darkMode ? StyleURL.Dark : StyleURL.Light) : StyleURL.Satellite);
                if (currentMapCameraState) {
                    setMapCenter(currentMapCameraState.properties.center);
                    setMapPitch(currentMapCameraState.properties.pitch);
                    setMapZoom(currentMapCameraState.properties.zoom - 0.01);
                    setTimeout(() => {
                        setMapZoom(currentMapCameraState.properties.zoom);
                    }, 100);
                }
            }, "layers-outline", mapMode === StyleURL.Satellite, findifyDataContext)}
        </View>

    </View>;
}

export const MapListItem = (t: any, item: any, index: number, findifyDataContext: FindifyDataContextType, setSelectedPOI: Function, setSelectedTabIndex: Function, navigation: any) => {
    const title = getTranslatedString(item.title, findifyDataContext);
    const resume = getTranslatedString(item.resume, findifyDataContext);
    const topLine = item.categories.map((category: any, index: number) => {
        const catName = getTranslatedString(category.category.name, findifyDataContext);
        if (index === 0) {
            return catName;

        } else {
            return ` | ${catName}`;
        }
    }).join('');

    return (
        <Pressable
            key={`news_index_${index}`}
            style={{
                padding: 6,
            }}
            onPress={item.onPress}
        >
            <View
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    ...DefaultShadow,
                    backgroundColor: PlaceColors(findifyDataContext).background.get(),
                    padding: 6,
                    borderWidth: 1,
                    borderRadius: 10,
                    gap: 6,
                }}
            >
                {item.image &&
                    <Image
                        style={{
                            width: 100,
                            borderRadius: 6,
                            objectFit: 'cover',
                            aspectRatio: 1
                        }}
                        source={{ uri: item.image }}
                    />
                }
                <View style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                    {topLine && <PlaceText
                        style={[{
                            color: PlaceColors(findifyDataContext).text.get(),
                            fontSize: 14,
                        }]}
                    >
                        {topLine}
                    </PlaceText>}
                    {title && <PlaceText
                        style={[{
                            color: PlaceColors(findifyDataContext).text.get(),
                            fontSize: 16,
                            fontWeight: 'bold',
                        }]}
                    >
                        {title}
                    </PlaceText>}
                    {resume && <PlaceText
                        style={[{
                            color: PlaceColors(findifyDataContext).text.get(),
                            fontSize: 14,
                        }]}
                        numberOfLines={2}
                        ellipsizeMode="tail"
                    >
                        {resume}
                    </PlaceText>}
                    <View
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            gap: 10,
                            marginTop: 10
                        }}
                    >
                        <Pressable style={{
                            paddingHorizontal: 10,
                            paddingVertical: 5,
                            borderRadius: 40,
                            backgroundColor: PlaceColors(findifyDataContext).background.get(),
                        }}
                            onPress={() => {
                                const scheme = Platform.select({ ios: 'maps://0,0?q=', android: 'geo:0,0?q=' });
                                const latLng = `${item.latitude},${item.longitude}`;
                                const label = getTranslatedString(item.title, findifyDataContext);
                                const url = Platform.select({
                                    ios: `${scheme}${label}@${latLng}`,
                                    android: `${scheme}${latLng}(${label})`
                                });

                                Linking.openURL(url);
                            }}>
                            <PlaceText style={{
                                color: PlaceColors(findifyDataContext).primary.get(),
                            }}>{t('map_tab.get_directions')}</PlaceText>
                        </Pressable>
                        <Pressable style={{
                            paddingHorizontal: 10,
                            paddingVertical: 5,
                            borderRadius: 40,
                            backgroundColor: PlaceColors(findifyDataContext).background.get(),
                        }}
                            onPress={() => {
                                setSelectedTabIndex(0);
                                OnMapPoiClick(item, setSelectedPOI, navigation, findifyDataContext);
                            }}>
                            <PlaceText style={{
                                color: PlaceColors(findifyDataContext).primary.get(),
                            }}>{t('map_tab.view_in_map')}</PlaceText>
                        </Pressable>
                    </View>
                </View>
            </View>

        </Pressable>
    );
}

const PlaceMapList = ({
    navigation,
    availableFilteredPois,
    setSelectedPOI,
    setSelectedTabIndex,
}: {
    setSelectedPOI: Function,
    setSelectedTabIndex: Function,
    availableFilteredPois: any[],
    navigation: any,
}) => {
    const findifyDataContext = useContext(FindifyDataContext);
    const insets = useSafeAreaInsets();
    const { t } = useTranslation();

    return <FlatList
        key={'eventsListAgenda'}
        data={availableFilteredPois.map((content: any) => {
            return {
                ...content,
                categories: content.categories,
                id: content.point_of_interest_id,
                image: getFeaturedImageUrl(content),
                title: getTranslatedString(content.title, findifyDataContext),
                resume: getTranslatedString(content.resume, findifyDataContext),
                onPress: () => {
                    navigation.push('PlaceContentDetails', {
                        module: 'points_of_interest',
                        content_id: content.point_of_interest_id,
                        page_slug: content.page_slug
                    })
                }
            }
        })}
        numColumns={1}
        showsVerticalScrollIndicator={false}
        showsHorizontalScrollIndicator={false}
        renderItem={({ item, index }) => {
            return MapListItem(t, item, index, findifyDataContext, setSelectedPOI, setSelectedTabIndex, navigation);
        }}
        bounces={false}
        keyExtractor={(item, index) => index.toString()}
        contentContainerStyle={[{
            paddingBottom: insets.bottom + BottomNavigationPadding,
        }]}
    />
}

export const PlaceMapTab = ({ navigation }: any) => {
    const findifyDataContext = useContext(FindifyDataContext);
    const [availablePOIS, setAvailablePOIS] = useState<any>([]);
    const [selectedCategories, setSelectedCategories] = useState<number[]>([]);
    const [availablePOISCategories, setAvailablePOISCategories] = useState<any>([]);
    const [availablePOISFiltered, setAvailablePOISFiltered] = useState<any>([]);
    const [selectedTabIndex, setSelectedTabIndex] = useState(0);
    const [CategoryFiltersOpen, setCategoryFiltersOpen] = useState(false);
    const [selectedPOI, setSelectedPOI] = useState<number | undefined>();

    const { t } = useTranslation();
    const insets = useSafeAreaInsets();
    const poisApi = getApiFromModule('points_of_interest', findifyDataContext);
    const categoriesApi = getApiFromModule('categories', findifyDataContext);
    const route = useRoute();
    const routeProps: any = route?.params ?? {};
    const focusedCoordinates = Number.isFinite(Number(routeProps.longitude)) && Number.isFinite(Number(routeProps.latitude))
        ? [Number(routeProps.longitude), Number(routeProps.latitude)] as Position
        : undefined;
    // selectedCategories, setSelectedCategories

    // Apply categories filter
    useEffect(() => {
        if (selectedCategories.length) {
            setAvailablePOISFiltered(availablePOIS.filter((poi: any) => {
                const poiCategories = poi.categories.map((c: any) => c.category_id);
                return hasMatchingNumbers(poiCategories, selectedCategories);
            }));

        } else {
            setAvailablePOISFiltered(availablePOIS)
        };
    }, [availablePOIS, selectedCategories])


    useEffect(() => {
        if (routeProps['map_category']) {
            setSelectedCategories([+routeProps.map_category]);
        }
        categoriesApi.getList(0, 100, null, { category_type: 'points_of_interest' }, null).then((data: any) => {
            setAvailablePOISCategories(data.results);
        });
        poisApi.getList(0, 1000, null, null, null).then((data: any) => {
            setAvailablePOIS(data.results.map((poi: any) => {
                const coords = getCoordsFromPOINT(poi.lat_long);
                if (!isNaN(coords.latitude) && !isNaN(coords.longitude)) {
                    return {
                        ...poi,
                        ...coords,
                    }
                }
            }).filter((poi: any) => poi !== undefined));
        });
    }, []);

    const renderScene = ({ route }: any) => {
        switch (route.key) {
            case 'map':
                return <PlaceMapElement
                    navigation={navigation}
                    availablePois={availablePOIS}
                    availablePOISFiltered={availablePOISFiltered}
                    availablePOISCategories={availablePOISCategories}
                    selectedCategories={selectedCategories}
                    setSelectedPOI={setSelectedPOI}
                    selectedPOI={selectedPOI}
                    focusedPoiId={routeProps.point_of_interest_id ? Number(routeProps.point_of_interest_id) : undefined}
                    focusedCoordinates={focusedCoordinates}
                    setSelectedCategories={setSelectedCategories} />;
            case 'list':
                return <PlaceMapList
                    setSelectedPOI={setSelectedPOI}
                    setSelectedTabIndex={setSelectedTabIndex}
                    availableFilteredPois={availablePOISFiltered}
                    navigation={navigation}
                />;
            default:
                return null;
        }
    };



    const routes = [
        {
            key: 'map',
            title: t('map_tab.map'),
        },
        {
            key: 'list',
            title: t('map_tab.list'),
        },
    ];

    return <BasePage navigation={navigation}>
        <View style={[styles.mapHeader, { paddingTop: insets.top + 10 }]}>
            <Pressable
                onPress={() => navigation?.canGoBack() ? navigation.goBack() : undefined}
                style={styles.mapHeaderBack}
            >
                <Icon name="chevron-back" size={42} color="#fff" />
            </Pressable>
            <PlaceText style={[styles.mapHeaderTitle, { color: PlaceColors(findifyDataContext).primary.get() }]}>
                {t('modules.map')}
            </PlaceText>
        </View>
        <View style={styles.mapTabs}>
            {routes.map((route, i) => {
                const selected = selectedTabIndex === i;

                return (
                    <Pressable
                        key={route.key}
                        style={[
                            styles.mapTabButton,
                            {
                                backgroundColor: selected ? PlaceColors(findifyDataContext).background.get() : PlaceColors(findifyDataContext).primary.get(),
                                borderColor: PlaceColors(findifyDataContext).text.get(),
                            },
                            DefaultShadow,
                        ]}
                        onPress={() => {
                            Toast.hide();
                            setSelectedPOI(undefined);
                            setSelectedTabIndex(i)
                        }}>
                        <PlaceText style={[
                            styles.mapTabText,
                            {
                                color: selected ? PlaceColors(findifyDataContext).primary.get() : PlaceColors(findifyDataContext).background.get(),
                            }
                        ]}>{route.title}</PlaceText>
                    </Pressable>
                );
            })}
        </View>
        <View style={{ flex: 1 }}>
            {renderScene({ route: routes[selectedTabIndex] })}
        </View>
        <View style={[styles.mapButtons, { bottom: insets.bottom + 60, right: 10 }]}>
            {makeFilterModal(findifyDataContext, CategoryFiltersOpen, setCategoryFiltersOpen, availablePOISCategories, selectedCategories, setSelectedCategories)}
        </View>
    </BasePage >;

};

const makeMapButton = (onPressIn: () => void, icon: string, isEnabled: boolean, findifyDataContext: any) => {
    return <Pressable
        onPressIn={onPressIn}
        style={[styles.mapButton, isEnabled ? styles.mapButtonSelected : {}, {
            backgroundColor: PlaceColors(findifyDataContext).primary.get()
        }]}
    >
        <Icon name={icon} size={24} color={isEnabled ? PlaceColors(findifyDataContext).primary.get() : "white"} />
    </Pressable>;
}

const makeFilterModal = (findifyDataContext: FindifyDataContextType | null, CategoryFiltersOpen: boolean, setCategoryFiltersOpen: Function, availablePOISCategories: any[], selectedCategories: number[], setSelectedCategories: Function) => {
    return <>
        <FindifyBasicModal
            isOpen={CategoryFiltersOpen}
            closeModal={() => {
                setCategoryFiltersOpen(false)
            }}
        >
            <View style={{
                backgroundColor: PlaceColors(findifyDataContext).background.get(),
                borderRadius: 10,
                overflow: 'hidden',
            }}>
                {availablePOISCategories.map((cat: any, index: number) => {
                    return <Pressable
                        key={'avaliableLanguages' + index}
                        onPress={() => {
                            if (selectedCategories.includes(cat.category_id)) {
                                setSelectedCategories(selectedCategories.filter((c: any) => c !== cat.category_id));
                            } else {
                                setSelectedCategories([...selectedCategories, cat.category_id]);
                            }
                        }}>
                        <View style={
                            {
                                padding: 10,
                                ...(index !== 0 ? { borderTopWidth: 0.5, borderTopColor: PlaceColors(findifyDataContext).text.withAlpha(128) } : {}),
                                ...(selectedCategories.includes(cat.category_id) ? { opacity: 0.5, backgroundColor: PlaceColors(findifyDataContext).alternateListBG.get() } : {})
                            }}>
                            <PlaceText
                                style={{
                                    color: PlaceColors(findifyDataContext).text.get(),
                                }}
                            >{getTranslatedString(cat.name, findifyDataContext)}</PlaceText>
                        </View>
                    </Pressable>
                })}
            </View>
        </FindifyBasicModal>
        {makeIconFilterButton(() => {
            setCategoryFiltersOpen(!CategoryFiltersOpen);
        }, "funnel-outline", selectedCategories.length > 0, findifyDataContext)}
    </>
}


const styles = StyleSheet.create({
    page: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    container: {
        flex: 1,
        // height: '100%',
        width: '100%',
    },
    mapHeader: {
        minHeight: 118,
        width: '100%',
        backgroundColor: '#d7f5fb',
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 12,
        gap: 14,
    },
    mapHeaderBack: {
        width: 72,
        height: 72,
        borderRadius: 36,
        backgroundColor: 'rgba(40, 68, 80, 0.35)',
        alignItems: 'center',
        justifyContent: 'center',
    },
    mapHeaderTitle: {
        fontSize: 44,
        lineHeight: 52,
        fontWeight: 'bold',
    },
    mapTabs: {
        height: 106,
        backgroundColor: '#e5f9fc',
        flexDirection: 'row',
        justifyContent: 'space-around',
        alignItems: 'center',
        paddingHorizontal: 90,
        borderBottomWidth: 0,
    },
    mapTabButton: {
        minWidth: 116,
        height: 56,
        paddingHorizontal: 22,
        borderRadius: 28,
        borderWidth: 2,
        alignItems: 'center',
        justifyContent: 'center',
    },
    mapTabText: {
        fontSize: 24,
        lineHeight: 30,
        fontWeight: '500',
    },
    mapButtons: {
        position: 'absolute',
        marginLeft: 10,
        gap: 10,
        zIndex: 1000,
        elevation: 1000,
    },
    mapButtonSelected: {
        backgroundColor: 'white'

    },
    mapButton: {
        padding: 4,
        borderRadius: 100,
    },
    mapCompassButton: {
        position: 'absolute',
        top: 136,
        right: 24,
        width: 74,
        height: 74,
        borderRadius: 37,
        backgroundColor: '#000',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        elevation: 1000,
        ...IconShadow,
    },
    map: {
        flex: 1
    }

})


const MapPlaceToast = (props: any) => {
    return <Pressable
        style={{
            width: '90%',
        }}
        onPress={props.onPress}>
        <View style={{
            backgroundColor: PlaceColors(props.findifyDataContext).background.get(),
            padding: 10,
            borderRadius: 5,
            display: 'flex',
            flexDirection: 'row',
            gap: 4,
            ...DefaultShadow
        }}>
            <Pressable
                onPress={() => {
                    props.onHide();
                    Toast.hide()
                }}
                style={{
                    position: 'absolute',
                    right: -10,
                    top: -10,
                    backgroundColor: PlaceColors(props.findifyDataContext).text.get(),
                    borderRadius: 100,
                    padding: 2,
                    ...IconShadow,
                }}
            >
                <Icon
                    style={{
                        color: PlaceColors(props.findifyDataContext).background.get(),
                    }}
                    name={'close'}
                    size={24}
                />
            </Pressable>
            {props.image &&
                <Image style={{
                    width: 60,
                    height: 60,
                    objectFit: 'cover'
                }} source={{ uri: props.image }} />
            }

            <View
                style={{
                    flex: 1,
                }}
            >
                <PlaceText style={{
                    color: PlaceColors(props.findifyDataContext).text.get(),
                    fontSize: 18
                }} >{props.title}</PlaceText>
                <PlaceText style={{
                    color: PlaceColors(props.findifyDataContext).text.get(),
                    fontSize: 14
                }} >{props.message}</PlaceText>
            </View>
        </View>
    </Pressable>
}
